<template lang="pug">
.contrastRatio
  .contrastRatio-logo
    AppIcon(name="logoContrast")

  .contrastRatio-side(:style="{'backgroundColor': foreColor.hex}")
    no-ssr
      chrome-picker(v-model="foreColor" disableAlpha)
    h5.contrastRatio-side-title(:style="{'color': backColor.hex}") {{$t('textColor')}}
  .contrastRatio-side(:style="{'backgroundColor': backColor.hex}")
    no-ssr
      chrome-picker(v-model="backColor" disableAlpha)
    h5.contrastRatio-side-title(:style="{'color': foreColor.hex}") {{$t('background')}}
  .contrastRatio-result
    div
      h5 {{Number(contrastRatio).toFixed(2)}}
    p #[span AA Normal:] #[span.u-cl-success(v-if="this.AANormal") Pass] #[span.u-cl-danger(v-else) Fail]
    p #[span AA Large:] #[span.u-cl-success(v-if="this.AALarge") Pass] #[span.u-cl-danger(v-else) Fail]
    p #[span AAA Normal:] #[span.u-cl-success(v-if="this.AAANormal") Pass] #[span.u-cl-danger(v-else) Fail]
    p #[span AAA Large:] #[span.u-cl-success(v-if="this.AAALarge") Pass] #[span.u-cl-danger(v-else) Fail]
    p #[span UI and Graphics:] #[span.u-cl-success(v-if="this.graphicsAndUI") Pass] #[span.u-cl-danger(v-else) Fail]
    button.button.is-block.is-tiny.u-mb-0.u-mt-ts(@click="swapColors") {{$t('swap')}}
</template>

<style lang="stylus">
$clMain = #611a95

// .footer, .header, .header-after
// display: none !important
.contrastRatio
  width: 100vw
  height: 100vh
  display: flex
  position: fixed
  top: 0
  right: 0
  bottom: 0
  left: 0

  +mq($until: 'desktop')
    height: auto
    position: static
    background: $clMain
    flex-wrap: wrap

  +mq($until: 'tablet')
    flex-direction: column
    align-items: flex-end

  &-side
    height: 100%
    width: 50%
    flex: 0 0 50%
    display: flex
    justify-content: center
    align-items: center
    min-height: 400px
    flex-direction: column
    position: relative

    &-title
      position: absolute
      bottom: 0
      left: 0
      right: 0
      text-align: center

    +mq($until: 'desktop')
      min-height: 440px

    +mq($until: 'tablet')
      width: 100%

    +mq($until: 'mobile')
      min-height: 360px

  &-result
    position: fixed
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)
    width: 225px
    height: auto
    background: $clWhite
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3)
    display: flex
    flex-direction: column
    padding: 20px
    text-align: center
    color: $clMain
    min-height: 245px
    justify-content: center

    +mq($until: 'desktop')
      position: static
      transform: none
      width: 100%

      h5
        font-size: 40px

    p
      font-size: 14px
      display: flex
      justify-content: space-between

  &-logo
    position: absolute
    top: unitRes(30, 60)
    left: 50%
    transform: translateX(-50%)
    color: $clWhite
    z-index: 2

    +mq($until: 'desktop')
      position: static
      transform: none
      width: 100%
      text-align: center
      margin: unitRes(30, 60) auto

    .icon
      width: unitRes(200, 280)
      height: unitRes(130, 181)
</style>

<script>
import { Chrome } from 'vue-color';
import NoSSR from 'vue-no-ssr';
import tinycolor from 'tinycolor2';
import { generateSeoMeta } from '~/utils/seo';

let components = {
  'chrome-picker': Chrome,
  'no-ssr': NoSSR
};
export default {
  layout: 'minimal',
  head: generateSeoMeta({ title: 'pageTitle', description: 'pageDesc' }, { i18n: true }),
  components: components,
  mounted() {
    this.foreColor.hex = '#14d2ff';
    this.backColor.hex = '#611a95';
    this.calcContrast();
  },
  data: () => ({
    foreColor: {
      hex: ''
    },
    backColor: {
      hex: ''
    },
    testMessage: null,
    AALarge: false,
    AANormal: false,
    AAALarge: false,
    AAANormal: false,
    graphicsAndUI: false,
    contrastRatio: null
  }),
  methods: {
    calcContrast() {
      this.contrastRatio = tinycolor.readability(this.foreColor.hex, this.backColor.hex);
    },
    swapColors() {
      let temp = null;
      temp = this.backColor.hex;
      this.backColor.hex = this.foreColor.hex;
      this.foreColor.hex = temp;
    }
  },
  watch: {
    'foreColor.hex'() {
      this.calcContrast();
    },
    'backColor.hex'() {
      this.calcContrast();
    },
    contrastRatio(to) {
      if (to >= 7) {
        this.AALarge = true;
        this.AANormal = true;
        this.AAALarge = true;
        this.AAANormal = true;
        this.graphicsAndUI = true;
      } else if (to >= 4.5 && to < 7) {
        this.AALarge = true;
        this.AANormal = true;
        this.AAALarge = true;
        this.AAANormal = false;
        this.graphicsAndUI = true;
      } else if (to >= 3 && to < 4.5) {
        this.AALarge = true;
        this.AANormal = false;
        this.AAALarge = false;
        this.AAANormal = false;
        this.graphicsAndUI = true;
      } else if (to < 3) {
        this.AALarge = false;
        this.AANormal = false;
        this.AAALarge = false;
        this.AAANormal = false;
        this.graphicsAndUI = false;
      }
    }
  }
};
</script>

<i18n>
{
  "en": {
    "pageTitle":"Contrast calculator",
    "pageDesc":"Test your color combinations and palettes. Calculate the contrast ratio to give your design depth and personality.",
    "textColor":"Text color",
    "background":"Background",
    "swap":"Swap"
  },
  "ar": {
    "pageTitle":"آلة حساب التباين",
    "pageDesc":"اختبر تركيبات الألوان واحسب التباين لإعطاء عمق للتصميم والشخصية من خلال الظلال وامنح تصميمك الروح واجعلها نابضة بالحياة",
    "textColor":"لون النص",
    "background":"الخلفية",
    "swap":"تبديل"
  }
}
</i18n>
